<template>
    <base-tool
        :tool-name="tool.name"
        :tool-description="tool.description"
        @back="handleBack"
    >
        <!-- Loading state -->
        <div v-if="loading" class="text-center py-3">
            <i class="el-icon-loading"></i> Loading...
        </div>

        <!-- Create / Edit form view -->
        <div v-else-if="showForm">
            <el-alert
                v-if="editingSequenceId && activeContactsCount > 0"
                :title="`${activeContactsCount} contact(s) are currently using this sequence.`"
                type="warning"
                :closable="false"
                show-icon
                class="mb-3"
            />

            <el-form label-position="top" size="small">
                <el-form-item label="Sequence Name" required>
                    <el-input
                        v-model="form.name"
                        placeholder="e.g. Support Intake Flow"
                        data-testid="sms-sequence-name-input"

                    />
                </el-form-item>

                <div class="mb-2">
                    <strong>Steps</strong>
                </div>

                <div
                    v-for="(step, index) in form.steps"
                    :key="index"
                    class="step-row mb-3 p-2 border rounded"
                >
                    <div class="d-flex justify-content-between align-items-center mb-1">
                        <span class="font-weight-bold" style="font-size: 12px;">Step {{ index + 1 }}</span>
                        <el-button
                            v-if="form.steps.length > 1"
                            type="text"
                            size="mini"
                            class="text-danger"
    
                            @click="removeStep(index)"
                        >
                            Remove
                        </el-button>
                    </div>

                    <el-form-item label="Messages" class="mb-1">
                        <div
                            v-for="(msg, messageIndex) in step.messages"
                            :key="messageIndex"
                            class="d-flex align-items-center mb-1"
                        >
                            <el-input
                                v-model="step.messages[messageIndex]"
                                placeholder="Message to send to the contact"
                                size="small"
                                class="flex-grow-1"
                            />
                            <el-button
                                type="text"
                                icon="el-icon-close"
                                size="mini"
                                class="ml-1 text-danger"
                                :disabled="messageIndex === 0"
                                @click="removeMessage(index, messageIndex)"
                            />
                        </div>
                        <el-button
                            type="text"
                            icon="el-icon-plus"
                            size="mini"
                            @click="addMessage(index)"
                        >
                            Add message
                        </el-button>
                    </el-form-item>

                    <el-form-item class="mb-1">
                        <template #label>
                            <span>Expected Response</span>
                            <el-tooltip placement="top" effect="dark">
                                <div slot="content" style="max-width: 280px;">
                                    Describe what kind of reply is expected to advance to the next step.<br><br>
                                    AI will evaluate if the contact's reply matches this criteria.<br><br>
                                    <b>Examples:</b><br>
                                    - "Any positive response"<br>
                                    - "A valid email address"<br>
                                    - "Any car model or brand"<br>
                                    - "A date or time preference"<br><br>
                                    Leave empty to advance on any reply.
                                </div>
                                <i class="el-icon-question ml-1" style="color: #909399; cursor: help;"></i>
                            </el-tooltip>
                        </template>
                        <el-input
                            v-model="step.expected_response"
                            placeholder="e.g. Any positive response"
    
                        />
                    </el-form-item>

                    <el-form-item class="mb-0">
                        <template #label>
                            <span>Contact Property</span>
                            <el-tooltip placement="top" effect="dark">
                                <div slot="content" style="max-width: 280px;">
                                    When set, the contact's reply will be extracted and saved to this property.<br><br>
                                    <b>Examples:</b><br>
                                    - Ask "What is your email?" &rarr; save to <b>email</b><br>
                                    - Ask "What is your name?" &rarr; save to <b>first_name</b><br><br>
                                    Requires "Expected Response" to be set. Leave empty if no data needs to be collected.
                                </div>
                                <i class="el-icon-question ml-1" style="color: #909399; cursor: help;"></i>
                            </el-tooltip>
                        </template>
                        <el-select
                            v-model="step.contact_property"
                            clearable
                            filterable
                            placeholder="Select a property"
                            class="w-100"
    
                        >
                            <el-option
                                v-for="property in contactProperties"
                                :key="property.slug"
                                :label="property.name"
                                :value="property.slug"
                            />
                        </el-select>
                    </el-form-item>
                </div>

                <el-button
                    type="text"
                    size="mini"
                    :disabled="isEditDisabled"
                    @click="addStep"
                >
                    + Add Step
                </el-button>

                <el-form-item label="Completion Message" required class="mt-3">
                    <el-input
                        v-model="form.completion_message"
                        type="textarea"
                        :rows="2"
                        placeholder="Message sent to the contact after the last step is completed"

                    />
                    <small class="text-muted">This message is always sent when the sequence finishes.</small>
                </el-form-item>

                <div class="d-flex justify-content-end mt-2">
                    <el-button
                        v-if="sequences.length > 0"
                        size="small"
                        @click="cancelForm"
                    >
                        Cancel
                    </el-button>
                    <el-button
                        type="primary"
                        size="small"
                        :loading="saving"

                        @click="saveSequence"
                    >
                        {{ editingSequenceId ? 'Save' : 'Save & Select' }}
                    </el-button>
                </div>
            </el-form>
        </div>

        <!-- Table list view -->
        <div v-else>
            <div class="sequence-list" style="max-height: 280px; overflow-y: auto;">
                <div
                    v-for="sequence in sequences"
                    :key="sequence.id"
                    class="sequence-row d-flex align-items-center justify-content-between p-2 mb-1 border rounded"
                    :class="{ 'sequence-row--hover': !deleting && !cloning }"
                >
                    <div
                        class="sequence-name flex-grow-1"
                        style="cursor: pointer; min-width: 0;"
                        @click="selectSequence(sequence)"
                    >
                        <span class="text-truncate d-block">{{ sequence.name }}</span>
                    </div>
                    <div class="sequence-actions flex-shrink-0 ml-2">
                        <el-button
                            type="text"
                            size="mini"
                            icon="el-icon-copy-document"
                            title="Clone"
                            :loading="cloning === sequence.id"
                            @click.stop="cloneSequence(sequence)"
                        />
                        <el-button
                            type="text"
                            size="mini"
                            icon="el-icon-edit"
                            title="Edit"
                            @click.stop="editSequence(sequence)"
                        />
                        <el-button
                            type="text"
                            size="mini"
                            icon="el-icon-delete"
                            class="text-danger"
                            title="Delete"
                            :loading="deleting === sequence.id"
                            @click.stop="deleteSequence(sequence)"
                        />
                    </div>
                </div>
            </div>

            <div class="text-center mt-2">
                <el-button
                    type="text"
                    size="small"
                    icon="el-icon-plus"
                    @click="startCreate"
                >
                    Create New SMS Sequence
                </el-button>
            </div>
        </div>
    </base-tool>
</template>

<script>
import BaseTool from './base-tool.vue'
import ToolMixin from './tool-mixin'

export default {
    name: 'SmsSequenceTool',

    mixins: [ToolMixin],

    components: {
        BaseTool
    },

    props: {
        tool: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            sequences: [],
            loading: true,
            showForm: false,
            saving: false,
            deleting: null,
            cloning: null,
            editingSequenceId: null,
            activeContactsCount: 0,
            contactProperties: [],
            form: {
                name: '',
                completion_message: '',
                steps: [this.emptyStep()]
            }
        }
    },

    mounted() {
        this.fetchSequences()
        this.fetchContactProperties()
    },

    methods: {
        emptyStep() {
            return {
                messages: [''],
                expected_response: null,
                contact_property: null
            }
        },

        addStep() {
            this.form.steps.push(this.emptyStep())
        },

        removeStep(index) {
            this.form.steps.splice(index, 1)
        },

        addMessage(stepIndex) {
            this.form.steps[stepIndex].messages.push('')
        },

        removeMessage(stepIndex, messageIndex) {
            this.form.steps[stepIndex].messages.splice(messageIndex, 1)
        },

        fetchSequences() {
            this.loading = true
            axios.get('/api/v1/aloai/sms-sequences')
                .then(response => {
                    this.sequences = response.data.data || []
                    if (this.sequences.length === 0) {
                        this.showForm = true
                    }
                })
                .catch(error => {
                    console.error('Failed to fetch SMS sequences:', error)
                })
                .finally(() => {
                    this.loading = false
                })
        },

        fetchContactProperties() {
            axios.get('/api/v1/aloai/contact-properties')
                .then(response => {
                    const default_properties = response.data.default_properties || []
                    const custom_properties = response.data.custom_properties || []
                    this.contactProperties = [...default_properties, ...custom_properties]
                })
                .catch(error => {
                    console.error('Failed to fetch contact properties:', error)
                })
        },

        selectSequence(sequence) {
            this.$emit('sms-sequence-selected', sequence.id, sequence)
        },

        startCreate() {
            this.editingSequenceId = null
            this.activeContactsCount = 0
            this.resetForm()
            this.showForm = true
        },

        editSequence(sequence) {
            this.editingSequenceId = sequence.id
            this.activeContactsCount = sequence.active_contacts_count || 0
            this.form = {
                name: sequence.name,
                completion_message: sequence.completion_message || '',
                steps: (sequence.steps || []).map(step => ({
                    messages: step.messages || [''],
                    expected_response: step.expected_response || null,
                    contact_property: step.contact_property || null
                }))
            }
            if (this.form.steps.length === 0) {
                this.form.steps = [this.emptyStep()]
            }
            this.showForm = true
        },

        cloneSequence(sequence) {
            this.cloning = sequence.id
            axios.post(`/api/v1/aloai/sms-sequences/${sequence.id}/clone`)
                .then(response => {
                    const cloned = response.data.data
                    this.sequences.push(cloned)
                    this.$message.success('SMS Sequence cloned.')
                    this.editSequence(cloned)
                })
                .catch(error => {
                    const message = error.response?.data?.message || 'Failed to clone SMS sequence.'
                    this.$message.error(message)
                })
                .finally(() => {
                    this.cloning = null
                })
        },

        deleteSequence(sequence) {
            this.deleting = sequence.id
            axios.delete(`/api/v1/aloai/sms-sequences/${sequence.id}`)
                .then(() => {
                    this.sequences = this.sequences.filter(s => s.id !== sequence.id)
                    this.$message.success('SMS Sequence deleted.')
                    if (this.sequences.length === 0) {
                        this.startCreate()
                    }
                })
                .catch(error => {
                    const message = error.response?.data?.message || 'Failed to delete SMS sequence.'
                    this.$message.error(message)
                })
                .finally(() => {
                    this.deleting = null
                })
        },

        cancelForm() {
            this.showForm = false
            this.editingSequenceId = null
            this.activeContactsCount = 0
            this.resetForm()
        },

        saveSequence() {
            if (!this.form.name || !this.form.name.trim()) {
                this.$message.error('Please enter a sequence name.')
                return
            }

            const valid_steps = this.form.steps.filter(step =>
                step.messages && step.messages.some(m => m && m.trim())
            )
            if (valid_steps.length === 0) {
                this.$message.error('Please add at least one step with a message.')
                return
            }

            if (!this.form.completion_message || !this.form.completion_message.trim()) {
                this.$message.error('Please enter a completion message.')
                return
            }

            this.saving = true

            const payload = {
                name: this.form.name,
                completion_message: this.form.completion_message,
                steps: valid_steps.map((step, index) => ({
                    order: index + 1,
                    messages: step.messages.filter(m => m && m.trim()),
                    expected_response: step.expected_response || null,
                    contact_property: step.contact_property || null
                }))
            }

            const request = this.editingSequenceId
                ? axios.put(`/api/v1/aloai/sms-sequences/${this.editingSequenceId}`, payload)
                : axios.post('/api/v1/aloai/sms-sequences', payload)

            request
                .then(response => {
                    const sequence = response.data.data

                    if (this.editingSequenceId) {
                        const index = this.sequences.findIndex(s => s.id === this.editingSequenceId)
                        if (index !== -1) {
                            this.$set(this.sequences, index, sequence)
                        }
                        this.$message.success('SMS Sequence updated.')
                    } else {
                        this.sequences.push(sequence)
                        this.$message.success('SMS Sequence created.')
                        this.showForm = false
                        this.resetForm()
                    }
                })
                .catch(error => {
                    const message = error.response?.data?.message || 'Failed to save SMS sequence.'
                    this.$message.error(message)
                })
                .finally(() => {
                    this.saving = false
                })
        },

        resetForm() {
            this.form = {
                name: '',
                completion_message: '',
                steps: [this.emptyStep()]
            }
        },

        handleBack() {
            if (this.showForm && this.sequences.length > 0) {
                this.cancelForm()
                return
            }
            this.goBack()
        }
    }
}
</script>

<style scoped>
.step-row {
    background-color: #f8f9fa;
}

.sequence-row {
    transition: background-color 0.15s;
}

.sequence-row--hover:hover {
    background-color: #f5f7fa;
}

.sequence-name:hover span {
    color: #409EFF;
}
</style>
